import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, money, fmtDate, onlyDigits } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Money, { PrivacyToggle } from "@/components/Money";
import {
  TrendingUp, AlertTriangle, Calendar, Wallet, ScanLine, Plus, MessageCircle, ChevronRight,
} from "lucide-react";

function Metric({ label, value, tone = "default", testid, icon: Icon }) {
  const tones = {
    default: "bg-white border-stone-200",
    warn: "bg-[color:var(--terracotta-light)] border-[color:var(--terracotta)]/30",
    danger: "bg-rose-50 border-rose-200",
    ok: "bg-emerald-50 border-emerald-200",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border ${tones[tone]} p-4`}>
      <div className="flex items-center gap-2 text-stone-500">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <div className="font-mono-num text-2xl font-bold text-stone-900 mt-1">{value}</div>
    </div>
  );
}

function ParcelaRow({ p, onWhats }) {
  const late = p.status === "atrasada";
  const today = p.status !== "atrasada" && p.status !== "paga";
  return (
    <div className="flex items-center justify-between gap-3 py-3 ledger-line">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-stone-900 truncate">{p.cliente_nome}</span>
          {late && <span className="stamp-overdue">Atrasada</span>}
          {today && <span className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--terracotta)] bg-[color:var(--terracotta-light)] px-2 py-0.5 rounded-full">Hoje</span>}
        </div>
        <div className="text-xs text-stone-500 truncate">
          Parcela {p.numero} • Venc: {fmtDate(p.vencimento)} • {p.descricao}
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono-num text-sm font-bold text-stone-900"><Money v={p.valor - p.valor_pago} /></div>
        <button
          data-testid={`btn-whats-${p.id}`}
          onClick={() => onWhats(p)}
          className="text-xs text-[color:var(--whatsapp-dark)] font-bold hover:underline inline-flex items-center gap-1 mt-1"
        >
          <MessageCircle className="w-3 h-3" /> Cobrar
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { session, isAdmin, isVendedor, isCobrador } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Vendedor (sem admin): sem painel — redireciona pra Clientes
  const shouldRedirect = isVendedor && !isAdmin && !isCobrador;

  useEffect(() => {
    if (shouldRedirect) { setLoading(false); return; }
    api.get("/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [shouldRedirect]);

  if (shouldRedirect) return <Navigate to="/clientes" replace />;

  const cobradorOnly = isCobrador && !isAdmin;

  const sendWhats = async (p) => {
    try {
      // fetch phone
      const c = await api.get(`/clientes/${p.cliente_id}`);
      const phone = onlyDigits(c.data.telefone);
      const tipo = p.status === "atrasada" ? "atrasado" : "vencendo_hoje";
      const t = await api.get(`/whatsapp/template`, {
        params: {
          tipo,
          cliente: c.data.nome,
          parcela: `${p.numero}`,
          valor: p.valor - p.valor_pago,
          vencimento: p.vencimento,
        },
      });
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(t.data.mensagem)}`;
      window.open(url, "_blank");
    } catch (_e) { /* ignore */ }
  };

  return (
    <div>
      <header className="mb-5 flex items-start justify-between gap-3" data-testid="app-header">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--terracotta)]">
            Olá, {session?.staff?.name?.split(" ")[0]}
            {" — "}
            {isAdmin ? "Admin" : [isVendedor && "Vendedor", isCobrador && "Cobrador"].filter(Boolean).join(" + ") || "Funcionário"}
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900">Seu Painel</h1>
          <p className="text-sm text-stone-500 mt-1">Acompanhe recebimentos, atrasos e vencimentos.</p>
        </div>
        <PrivacyToggle />
      </header>

      {/* Quick actions — só admin ou vendedor combinado com admin */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        {(isAdmin || (isVendedor && !cobradorOnly)) && !cobradorOnly && (
          <>
            <Link to="/scan" data-testid="btn-quick-scan-ai" className="btn-primary text-sm">
              <ScanLine className="w-4 h-4" /> Escanear Carnê
            </Link>
            <Link to="/nova-venda" data-testid="btn-nova-venda" className="btn-primary text-sm" style={{ background: "var(--coal)" }}>
              <Plus className="w-4 h-4" /> Nova Venda
            </Link>
          </>
        )}
      </div>

      {loading && <div className="text-stone-500 text-sm">Carregando…</div>}

      {data && (
        <>
          <div className={`grid gap-3 mb-6 ${cobradorOnly ? "grid-cols-2" : "grid-cols-2"}`}>
            {!cobradorOnly && (
              <Metric
                testid="dashboard-total-receivables"
                label="A Receber"
                value={<Money v={data.total_a_receber} />}
                tone="warn"
                icon={Wallet}
              />
            )}
            <Metric
              testid="dashboard-overdue-amount"
              label="Em Atraso"
              value={<Money v={data.total_atrasado} />}
              tone={data.total_atrasado > 0 ? "danger" : "default"}
              icon={AlertTriangle}
            />
            {!cobradorOnly && (
              <Metric
                testid="dashboard-received-month"
                label="Recebido no Mês"
                value={<Money v={data.recebido_mes} />}
                tone="ok"
                icon={TrendingUp}
              />
            )}
            <Metric
              testid="dashboard-today-due"
              label="Vencem Hoje"
              value={`${data.vencimentos_hoje.length}`}
              icon={Calendar}
            />
          </div>

          {data.parcelas_atrasadas.length > 0 && (
            <section className="carne-card p-4 mb-5 animate-slide-up" data-testid="section-atrasadas">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-display text-lg font-bold text-stone-900">Cobrar em atraso</h2>
                <span className="text-xs font-bold text-rose-700 bg-rose-100 px-2 py-1 rounded-full pulse-urgent">
                  {data.parcelas_atrasadas.length}
                </span>
              </div>
              <div className="divide-y divide-stone-100">
                {data.parcelas_atrasadas.slice(0, 5).map((p) => (
                  <ParcelaRow key={p.id} p={p} onWhats={sendWhats} />
                ))}
              </div>
            </section>
          )}

          <section className="carne-card p-4 mb-5" data-testid="section-hoje">
            <h2 className="font-display text-lg font-bold text-stone-900 mb-2">Vencimentos de hoje</h2>
            {data.vencimentos_hoje.length === 0 ? (
              <p className="text-sm text-stone-500 py-3">Nada vencendo hoje. Bom dia de trabalho! ☕</p>
            ) : (
              <div className="divide-y divide-stone-100">
                {data.vencimentos_hoje.map((p) => (
                  <ParcelaRow key={p.id} p={{ ...p, status: "hoje" }} onWhats={sendWhats} />
                ))}
              </div>
            )}
          </section>

          <section className="carne-card p-4 mb-5" data-testid="section-proximos" style={{ display: cobradorOnly ? "none" : undefined }}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display text-lg font-bold text-stone-900">Próximos 7 dias</h2>
              <Link to="/clientes" className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1">
                Ver clientes <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            {data.proximos_7_dias.length === 0 ? (
              <p className="text-sm text-stone-500 py-3">Sem parcelas nos próximos 7 dias.</p>
            ) : (
              <div className="divide-y divide-stone-100">
                {data.proximos_7_dias.map((p) => (
                  <ParcelaRow key={p.id} p={p} onWhats={sendWhats} />
                ))}
              </div>
            )}
          </section>

          <div className="text-center text-xs text-stone-400 pb-4">
            {data.total_clientes} clientes cadastrados
          </div>
        </>
      )}
    </div>
  );
}
